import * as _ from 'lodash-es';
import Bold from 'quill/formats/bold';
import QuillHeader from 'quill/formats/header';
import deltaToText from '../utils/deltaToText';
import HeaderId, { generateHeaderId, parseHeaderId } from './HeaderId';

const HEADER_ANCHOR_ID_ATTRIBUTE = 'data-anchor-id';

export default class BasicHeader extends QuillHeader {
  static tagName = ['h1', 'h2', 'h3'];

  static create(value: string) {
    return super.create(parseInt(value, 10) > 3 ? 3 : value);
  }

  static getDOMSelector(fragment: string) {
    const selectors = [
      `[${HEADER_ANCHOR_ID_ATTRIBUTE}="${fragment}"]`,
      `[${HEADER_ANCHOR_ID_ATTRIBUTE}$="-${fragment}"]`,
    ];
    const headerId = parseHeaderId(fragment);
    if (headerId) {
      selectors.push(`[${HEADER_ANCHOR_ID_ATTRIBUTE}^="${headerId}-"]`);
    }
    return selectors.map((s) => `:scope > ${s}`).join(',');
  }

  protected anchorIdEnabled = false;

  clone() {
    const clone = super.clone();
    if (this.anchorIdEnabled && clone.domNode instanceof HTMLElement) {
      clone.domNode.removeAttribute(HEADER_ANCHOR_ID_ATTRIBUTE);
      if (HeaderId.value(clone.domNode)) {
        HeaderId.add(clone.domNode, generateHeaderId());
      }
    }
    return clone;
  }

  enforceAllowedChildren() {
    this.children.forEach((child) => {
      if (child instanceof Bold) {
        child.format(Bold.blotName, null);
      }
    });
  }

  getAnchorId() {
    const text = _.kebabCase(this.getText());
    let slug = text
      .replace(/[.,/#!$%^&*;:{}=\-_`~() ]/g, '-') // replace punctuation https://stackoverflow.com/questions/4328500/how-can-i-strip-all-punctuation-from-a-string-in-javascript-using-regex
      .replace(/-{2,}/g, '-');
    const id = this.formats()[HeaderId.attrName];
    if (id) return `${id}-${slug}`;

    // Below is all for backwards compatibility prior to HeaderId support
    slug = _.trim(slug, '-') || 'h';
    if (/^\d/.test(slug)) {
      slug = `s-${slug}`; // selectors cannot start with number
    }
    const nodes = Array.from(
      document.querySelectorAll(`#${slug}, *[id^="${slug}-"]`),
    );
    const count = nodes.reduce((sum, node) => {
      if (
        // eslint-disable-next-line no-bitwise
        this.domNode.compareDocumentPosition(node) &
          Node.DOCUMENT_POSITION_PRECEDING ||
        !this.domNode.parentNode?.contains(node)
      ) {
        return sum + 1;
      }
      return sum;
    }, 0);
    return count === 0 ? slug : `${slug}-${count}`;
  }

  getText() {
    return deltaToText(this.delta()).trim();
  }

  optimize() {
    // @ts-expect-error
    super.optimize();
    if (this.anchorIdEnabled) {
      const id = this.getAnchorId();
      if (this.domNode.getAttribute(HEADER_ANCHOR_ID_ATTRIBUTE) !== id) {
        this.domNode.setAttribute(HEADER_ANCHOR_ID_ATTRIBUTE, id);
      }
    }
  }
}
